import { Inject, Injectable, NgModule, PLATFORM_ID } from '@angular/core';
import { NAVIGATOR, WINDOW } from '@ng-web-apis/common';
import { from, ReplaySubject } from 'rxjs';
import { SwUpdate } from '@angular/service-worker';
import { AlertDialogData, DialogService } from '@madeinlune/dialog';
import { CommonModule, isPlatformBrowser } from '@angular/common';

@NgModule({
  imports: [CommonModule],
})
export class PwaModule {}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;

  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

@Injectable({
  providedIn: PwaModule,
})
export class PwaService {
  installLater$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  installed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  canInstall$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  serviceWorkerRegistration: ServiceWorkerRegistration | undefined;
  installLater!: boolean;
  #deferredPwaPromptEvent!: BeforeInstallPromptEvent | null;

  constructor(
    private swUpdate: SwUpdate,
    public dialog: DialogService,
    @Inject(WINDOW) private window: Window,
    @Inject(NAVIGATOR) private navigator: Navigator,
    @Inject(PLATFORM_ID) private platformId
  ) {
    if (isPlatformBrowser(this.platformId) && this.navigator.serviceWorker) {
      from(this.navigator.serviceWorker.getRegistration()).subscribe({
        next: (result) => {
          this.serviceWorkerRegistration = result;
        },
        error: (error) => {
          console.error('PwaService', { error });
        },
      });
      const installed = !!(
        this.window.matchMedia('(display-mode: standalone)').matches ||
        this.navigator['standalone']
      );
      this.installed$.next(installed);
    }

    swUpdate.versionUpdates.subscribe((evt) => {
      switch (evt.type) {
        case 'VERSION_DETECTED':
          console.log(`Downloading new app version: ${evt.version.hash}`);
          break;
        case 'VERSION_READY':
          console.log(`VERSION_READY`);
          this.#openUpdateWindow();
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.log(
            `Failed to install app version '${evt.version.hash}': ${evt.error}`
          );
          break;
      }
    });

    swUpdate.unrecoverable.subscribe((unrecoverable) => {
      console.log('swUpdate.unrecoverable', { unrecoverable });
    });

    /*fromEvent(this.window, 'beforeinstallprompt').subscribe((event: Event) => {
      this.openInstallWindow(event as BeforeInstallPromptEvent);
      this.canInstall$.next(true);
    });*/

    /*setTimeout(() => {
      this.#openInstallWindow();
    }, 4000);*/

    /*setTimeout(() => {
      this.#openUpdateWindow();
    }, 4000);*/
  }

  checkInstalled() {}

  openInstallWindow(event: BeforeInstallPromptEvent): void {
    event.preventDefault();
    this.#deferredPwaPromptEvent = event;
    this.#openInstallWindow();
  }

  async install() {
    if (this.#deferredPwaPromptEvent) {
      // deferredPrompt is a global variable we've been using in the sample to capture the `beforeinstallevent`
      this.#deferredPwaPromptEvent.prompt();
      // Find out whether the user confirmed the installation or not
      const { outcome } = await this.#deferredPwaPromptEvent.userChoice;
      // The deferredPrompt can only be used once.
      this.#deferredPwaPromptEvent = null;
      // Act on the user's choice
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt.');
      } else if (outcome === 'dismissed') {
        console.log('User dismissed the install prompt');
      }
    }
  }

  unInstall() {
    if (this.serviceWorkerRegistration) {
      from(this.serviceWorkerRegistration.unregister()).subscribe({
        next: (result) => {
          this.serviceWorkerRegistration = undefined;
        },
        error: (error) => {
          console.error({ error });
        },
      });
    }
  }

  #openInstallWindow(): void {
    if (!this.installLater) {
      const alertData: AlertDialogData = {
        message: 'pwa.install.message',
        actions: [
          {
            label: 'actions.install',
            id: 'install',
            main: true,
          },
          {
            label: 'actions.later',
            id: 'later',
          },
        ],
      };
      const dialogRef = this.dialog.openNotification(alertData, false);
      if (dialogRef) {
        dialogRef.closed.subscribe((action) => {
          if (action?.id === 'install') {
            this.installLater$.next(false);
            this.install();
          } else {
            this.installLater$.next(true);
          }
        });
      }
    }
  }

  #openUpdateWindow(): void {
    const alertData: AlertDialogData = {
      message: 'pwa.update.message',
      actions: [
        {
          label: 'actions.reload',
          id: 'reload',
          main: true,
        },
        {
          label: 'actions.later',
          id: 'later',
        },
      ],
    };
    const dialogRef = this.dialog.openNotification(alertData);
    if (dialogRef) {
      dialogRef.closed.subscribe((action) => {
        if (action?.id === 'reload') {
          this.window.location.reload();
        }
      });
    }
  }
}
